/**
 * Klaviyo form validation: name (letters/spaces only) and phone (digits only).
 * Attaches to forms injected by Klaviyo into .klaviyo-form-* containers.
 */
(function () {
  'use strict';

  const DATA_ATTACHED = 'data-klaviyo-validation-attached';
  const ERROR_MSG_CLASS = 'klaviyo-field-error';
  const NAME_ERROR_MSG = 'Numbers and special characters are not allowed.';
  const PHONE_ERROR_MSG = 'Alphabets and special characters are not allowed. Use numbers only.';

  function getOrCreateErrorEl(input) {
    var id = input.getAttribute('data-klaviyo-error-id');
    if (id) {
      var existing = document.getElementById(id);
      if (existing) return existing;
    }
    id = 'klaviyo-error-' + Date.now() + '-' + Math.random().toString(36).slice(2);
    input.setAttribute('data-klaviyo-error-id', id);
    var el = document.createElement('span');
    el.id = id;
    el.className = ERROR_MSG_CLASS;
    el.setAttribute('role', 'alert');
    el.style.display = 'none';
    if (input.nextSibling) {
      input.parentNode.insertBefore(el, input.nextSibling);
    } else {
      input.parentNode.appendChild(el);
    }
    return el;
  }

  function showError(input, message) {
    var el = getOrCreateErrorEl(input);
    el.textContent = message;
    el.style.display = 'block';
    input.setAttribute('aria-describedby', el.id);
  }

  function hideError(input) {
    var id = input.getAttribute('data-klaviyo-error-id');
    if (id) {
      var el = document.getElementById(id);
      if (el) el.style.display = 'none';
      input.removeAttribute('aria-describedby');
    }
  }

  // Name: letters (A-Za-z), spaces, and apostrophe (e.g. O'Brien)
  const NAME_REGEX = /[^A-Za-z\s']/g;
  function filterNameValue(value) {
    return String(value).replace(NAME_REGEX, '');
  }
  function isNameValid(value) {
    return value === filterNameValue(value);
  }

  // Phone: optional leading +, then digits only
  const PHONE_REGEX = /[^\d+]/g;
  const PHONE_VALID_REGEX = /^\+?\d*$/;
  function filterPhoneValue(value) {
    const s = String(value).replace(PHONE_REGEX, '');
    // Allow only one leading +
    if (s.indexOf('+') > 0) return s.replace(/\+/g, '');
    return s;
  }
  function isPhoneValid(value) {
    return PHONE_VALID_REGEX.test(String(value));
  }

  function getKlaviyoContainers() {
    return document.querySelectorAll('[class^="klaviyo-form-"]');
  }

  function findForm(el) {
    if (!el || !el.querySelector) return null;
    const form = el.querySelector('form');
    if (form) return form;
    const firstForm = el.closest ? el.closest('form') : null;
    return firstForm || null;
  }

  function getFormRoot(form) {
    return form.closest('[class^="klaviyo-form-"]') || form;
  }

  function looksLikePhoneField(input) {
    var n = (input.getAttribute('name') || '').toLowerCase();
    var placeholder = (input.getAttribute('placeholder') || '').toLowerCase();
    var ariaLabel = (input.getAttribute('aria-label') || '').toLowerCase();
    var id = (input.getAttribute('id') || '').toLowerCase();
    if (n.includes('phone') || n.includes('tel') || placeholder.includes('phone') || placeholder.includes('number') || ariaLabel.includes('phone') || id.includes('phone')) return true;
    var label = input.id && root.querySelector('label[for="' + input.id + '"]');
    if (label && /phone|number|tel/i.test(label.textContent)) return true;
    return false;
  }

  function findPhoneInputs(root) {
    var byTel = root.querySelectorAll('input[type="tel"]');
    if (byTel.length) return Array.from(byTel);
    var byName = Array.from(root.querySelectorAll('input[name*="phone"], input[name*="tel"]'));
    if (byName.length > 0) return byName;
    var textInputs = root.querySelectorAll('input[type="text"]');
    return Array.from(textInputs).filter(function (input) {
      var n = (input.getAttribute('name') || '').toLowerCase();
      var placeholder = (input.getAttribute('placeholder') || '').toLowerCase();
      var ariaLabel = (input.getAttribute('aria-label') || '').toLowerCase();
      if (n.includes('phone') || n.includes('tel')) return true;
      if (placeholder.includes('phone') || (placeholder.includes('number') && placeholder.includes('phone'))) return true;
      if (ariaLabel.includes('phone')) return true;
      return false;
    });
  }

  function findNameInputs(root) {
    var phoneInputs = findPhoneInputs(root);
    var isPhone = function (input) {
      return phoneInputs.indexOf(input) !== -1 || input.type === 'tel' || (input.getAttribute('name') || '').toLowerCase().includes('phone');
    };
    var byName = root.querySelectorAll(
      'input[name*="first_name"], input[name*="last_name"], input[name*="name"], input[name*="first-name"], input[name*="last-name"]'
    );
    var nameCandidates = Array.from(byName).filter(
      function (input) {
        return input.type !== 'email' && !(input.getAttribute('name') || '').toLowerCase().includes('email') && !isPhone(input);
      }
    );
    if (nameCandidates.length > 0) return nameCandidates;
    // Fallback: any text input that isn't email/phone (e.g. single "Name" field with different name attr)
    var textInputs = root.querySelectorAll('input[type="text"]');
    return Array.from(textInputs).filter(function (input) {
      var n = (input.getAttribute('name') || '').toLowerCase();
      return !n.includes('email') && !n.includes('phone') && !isPhone(input);
    });
  }

  function attachNameValidation(input) {
    if (input.hasAttribute(DATA_ATTACHED)) return;
    input.setAttribute(DATA_ATTACHED, 'true');

    input.addEventListener('keydown', function (e) {
      if (e.ctrlKey || e.metaKey) return;
      var key = e.key;
      if (key.length === 1 && !/[A-Za-z\s']/.test(key)) {
        e.preventDefault();
      }
    });

    input.addEventListener('paste', function (e) {
      e.preventDefault();
      var pasted = (e.clipboardData || window.clipboardData).getData('text');
      var filtered = filterNameValue(pasted);
      var el = this;
      var start = el.selectionStart;
      var end = el.selectionEnd;
      var val = el.value;
      var newVal = val.slice(0, start) + filtered + val.slice(end);
      el.value = filterNameValue(newVal);
      var newLen = el.value.length;
      el.setSelectionRange(Math.min(start + filtered.length, newLen), Math.min(start + filtered.length, newLen));
    });

    input.addEventListener('input', function () {
      var start = this.selectionStart;
      var end = this.selectionEnd;
      var filtered = filterNameValue(this.value);
      if (this.value !== filtered) {
        this.value = filtered;
        var len = filtered.length;
        this.setSelectionRange(Math.min(start, len), Math.min(end, len));
      }
      this.setCustomValidity('');
      this.removeAttribute('aria-invalid');
      hideError(this);
    });

    input.addEventListener('blur', function () {
      var val = this.value.trim();
      if (val && !isNameValid(this.value)) {
        this.setCustomValidity('Please use only letters and spaces.');
        this.setAttribute('aria-invalid', 'true');
        showError(this, NAME_ERROR_MSG);
      } else {
        this.setCustomValidity('');
        this.removeAttribute('aria-invalid');
        hideError(this);
      }
    });
  }

  function attachPhoneValidation(input) {
    if (input.hasAttribute(DATA_ATTACHED)) return;
    input.setAttribute(DATA_ATTACHED, 'true');

    input.addEventListener('input', function () {
      var start = this.selectionStart;
      var filtered = filterPhoneValue(this.value);
      if (this.value !== filtered) {
        this.value = filtered;
        this.setSelectionRange(Math.min(start, filtered.length), Math.min(start, filtered.length));
      }
      this.setCustomValidity('');
      this.removeAttribute('aria-invalid');
      hideError(this);
    });

    input.addEventListener('blur', function () {
      var val = this.value.trim();
      if (val && !isPhoneValid(this.value)) {
        this.setCustomValidity('Please use only numbers (and + for country code).');
        this.setAttribute('aria-invalid', 'true');
        showError(this, PHONE_ERROR_MSG);
      } else {
        this.setCustomValidity('');
        this.removeAttribute('aria-invalid');
        hideError(this);
      }
    });
  }

  function attachInputsInRoot(root) {
    findNameInputs(root).forEach(attachNameValidation);
    findPhoneInputs(root).forEach(attachPhoneValidation);
  }

  function attachToForm(form) {
    const root = getFormRoot(form);
    if (root.getAttribute(DATA_ATTACHED) === 'true') return;
    root.setAttribute(DATA_ATTACHED, 'true');

    attachInputsInRoot(root);

    var formObserver = new MutationObserver(function () {
      attachInputsInRoot(root);
    });
    formObserver.observe(root, { childList: true, subtree: true });

    form.addEventListener('submit', function (e) {
      var nameInputs = findNameInputs(root);
      var phoneInputs = findPhoneInputs(root);
      var firstInvalid = null;

      nameInputs.forEach(function (input) {
        if (input.value.trim() && !isNameValid(input.value)) {
          input.setCustomValidity('Please use only letters and spaces.');
          input.setAttribute('aria-invalid', 'true');
          showError(input, NAME_ERROR_MSG);
          if (!firstInvalid) firstInvalid = input;
        } else {
          hideError(input);
        }
      });
      phoneInputs.forEach(function (input) {
        if (input.value.trim() && !isPhoneValid(input.value)) {
          input.setCustomValidity('Please use only numbers (and + for country code).');
          input.setAttribute('aria-invalid', 'true');
          showError(input, PHONE_ERROR_MSG);
          if (!firstInvalid) firstInvalid = input;
        } else {
          hideError(input);
        }
      });

      if (firstInvalid) {
        e.preventDefault();
        firstInvalid.focus();
      }
    });
  }

  function scanContainers() {
    getKlaviyoContainers().forEach(function (container) {
      const form = container.querySelector('form') || findForm(container);
      if (form) attachToForm(form);
    });
  }

  function init() {
    scanContainers();
    setTimeout(scanContainers, 500);
    setTimeout(scanContainers, 2000);

    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          const form = node.tagName === 'FORM' ? node : findForm(node);
          if (form) attachToForm(form);
          const containers = node.querySelectorAll ? node.querySelectorAll('[class^="klaviyo-form-"]') : [];
          containers.forEach(function (c) {
            const f = c.querySelector('form');
            if (f) attachToForm(f);
          });
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
